import { useCallback, useState } from 'react';
import { Dropdown, Popover } from 'components/ui';
import { BarChartOutlined } from '@ant-design/icons';
import { StyledButton } from '../ImageViewer.style';

import { AtlasGqlImageFilter, useGetImageFiltersQuery } from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { Levels } from './Levels';
import { IMAGE_VIEWER_LEVELS } from 'utils/release-toggles';
import { ImageProps } from '../Image';

const NO_FILTER = 'no-filter';
const ENHANCE = 'enhance';
const ORIGINAL = 'original';
const LEVELS = 'levels';

export type FiltersProps = {
  onChange: (data: any) => void;
  onClickLevels: () => void;
  value?: string;
  levels?: { a?: number; b?: number };
  pictureId?: string;
  pictureGlobalId?: string;
  refetch: ImageProps['refetch'];
};

export const Filters: React.FunctionComponent<FiltersProps> = props => {
  const { onChange, onClickLevels, value = NO_FILTER } = props;
  const { hasReleaseToggle } = useAccountContext();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [levelsOpen, setLevelsOpen] = useState<boolean>(false);

  const { data, loading: imageFiltersLoading } = useGetImageFiltersQuery();
  const imageFilters = data?.imageFilters;

  const hasToggleImageEnhanceToggle = hasReleaseToggle('toggle-image-enhance');
  const hasDefaultImageEnhance = hasReleaseToggle('default-image-enhance');
  const hasImageViewerLevels = hasReleaseToggle(IMAGE_VIEWER_LEVELS);

  // TODO: when we remove both relese toggles we can remove `filteredImageFilters` and replace its references with `imageFilters`.
  // Currently the enhance and original filters show as an option, but will throw errors if you select them. To be addressed in upcoming work - AN 11/14/23 CRS 11/27/23
  const reworkFiltersForPostProcessing = () => {
    // If we have `default-image-enhance` on: remove 'enhance' so only 'original' is there
    if (hasDefaultImageEnhance) {
      return imageFilters?.filter(filterOption => filterOption && filterOption.id !== ENHANCE);
    }
    // We know `default-image-enhance` is off. If we have `toggle-image-enhance` on: remove 'original' and keep 'enchance'
    if (hasToggleImageEnhanceToggle) {
      return imageFilters?.filter(filterOption => filterOption && filterOption.id !== ORIGINAL);
    }

    // We know both post processing RTs are off: strip out both 'enhance' and 'original'
    return imageFilters?.filter(
      filterOption => filterOption && ![ENHANCE, ORIGINAL].includes(filterOption?.id)
    );
  };

  const filteredImageFilters: AtlasGqlImageFilter[] = (
    reworkFiltersForPostProcessing() ?? []
  ).filter((filter): filter is AtlasGqlImageFilter => !!filter);

  const defaultImageFilter: AtlasGqlImageFilter = { id: NO_FILTER, label: 'No Filter' };

  const handleToggleLevels = () => {
    setLevelsOpen(open => !open);
  };
  const levelsFilter = {
    id: LEVELS,
    label: (
      <Popover
        content={<Levels {...props} toggleOpen={handleToggleLevels} menuOpen={menuOpen} />}
        open={levelsOpen}
        placement="leftTop"
        trigger="click"
        arrow={false}
      >
        <span>Levels</span>
      </Popover>
    ),
  };

  const filterItems = [
    defaultImageFilter,
    ...filteredImageFilters,
    ...(hasImageViewerLevels ? [levelsFilter] : []),
  ].map(filter => {
    const { id, label } = filter;
    return {
      key: id,
      label: label,
      disabled: id === value,
    };
  });

  const handleClick = useCallback(
    ({ key }) => {
      if (!key.includes(LEVELS)) {
        onChange({ filter: key === NO_FILTER ? null : key });
      } else if (key === LEVELS) {
        onClickLevels();
        handleToggleLevels();
      }
    },
    [onChange]
  );

  return (
    <Dropdown
      menu={{
        items: filterItems,
        onClick: handleClick,
      }}
      trigger={['click']}
      open={menuOpen}
      onOpenChange={(open: boolean, { source }) => {
        if (source === 'trigger') {
          if (!open) {
            setLevelsOpen(false);
          }
          setMenuOpen(open);
        }
      }}
    >
      <StyledButton
        data-testid="image-controls-filter"
        title="Filter"
        disabled={imageFiltersLoading}
      >
        <BarChartOutlined />
      </StyledButton>
    </Dropdown>
  );
};
